/**
 * Supabase backend for Echomotion Whiteboard
 * Replaces the Firebase backend (data/firebase.ts)
 *
 * Tables (schema: whiteboard):
 *   whiteboard.rooms    (id text PK, iv text, ciphertext text, scene_version int, updated_at timestamptz)
 *   whiteboard.files    (id text PK, room_id text, mime_type text, data_url text, created_at timestamptz)
 */

import { reconcileElements } from "@excalidraw/excalidraw";
import { MIME_TYPES, toBrandedType } from "@excalidraw/common";
import { decompressData } from "@excalidraw/excalidraw/data/encode";
import {
  encryptData,
  decryptData,
} from "@excalidraw/excalidraw/data/encryption";
import { restoreElements } from "@excalidraw/excalidraw/data/restore";
import { getSceneVersion } from "@excalidraw/element";

import type { RemoteExcalidrawElement } from "@excalidraw/excalidraw/data/reconcile";
import type {
  ExcalidrawElement,
  FileId,
  OrderedExcalidrawElement,
} from "@excalidraw/element/types";
import type {
  AppState,
  BinaryFileData,
  BinaryFileMetadata,
  DataURL,
} from "@excalidraw/excalidraw/types";

import { FILE_CACHE_MAX_AGE_SEC } from "../app_constants";
import { getSyncableElements } from ".";

import type { SyncableExcalidrawElement } from ".";
import type Portal from "../collab/Portal";
import type { Socket } from "socket.io-client";

// ─────────────────────────────────────────────────────────────
// Config
// ─────────────────────────────────────────────────────────────

const SUPABASE_URL = import.meta.env.VITE_APP_SUPABASE_URL as string;
const SUPABASE_ANON_KEY = import.meta.env.VITE_APP_SUPABASE_ANON_KEY as string;
// Tables use whiteboard_ prefix to avoid naming conflicts in the public schema
const ROOMS_TABLE = "whiteboard_rooms";
const FILES_TABLE = "whiteboard_files";
const BASE64_CHUNK_SIZE = 0x8000;

const bytesToBase64 = (bytes: Uint8Array): string => {
  let binary = "";
  for (let i = 0; i < bytes.length; i += BASE64_CHUNK_SIZE) {
    binary += String.fromCharCode(...bytes.subarray(i, i + BASE64_CHUNK_SIZE));
  }
  return btoa(binary);
};

const base64ToBytes = (base64: string): Uint8Array => {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
};

// ─────────────────────────────────────────────────────────────
// Minimal REST helper (no SDK dependency — keeps bundle lean)
// ─────────────────────────────────────────────────────────────

interface SupabaseRow {
  [key: string]: unknown;
}

async function supabaseSelect<T = SupabaseRow>(
  table: string,
  filter: string,
  select = "*",
): Promise<T | null> {
  const res = await fetch(
    `${SUPABASE_URL}/rest/v1/${table}?${filter}&select=${select}`,
    {
      headers: {
        apikey: SUPABASE_ANON_KEY,
        Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
        // public schema = default, no Accept-Profile needed
      },
    },
  );
  if (!res.ok) return null;
  const data = await res.json();
  return Array.isArray(data) ? data[0] ?? null : data;
}

async function supabaseUpsert(
  table: string,
  payload: SupabaseRow,
): Promise<boolean> {
  const res = await fetch(`${SUPABASE_URL}/rest/v1/${table}`, {
    method: "POST",
    headers: {
      apikey: SUPABASE_ANON_KEY,
      Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
      "Content-Type": "application/json",
      Prefer: "resolution=merge-duplicates",
    },
    body: JSON.stringify(payload),
  });
  return res.ok || res.status === 201;
}

// ─────────────────────────────────────────────────────────────
// Encryption helpers (identical to firebase.ts)
// ─────────────────────────────────────────────────────────────

const encryptElements = async (
  key: string,
  elements: readonly ExcalidrawElement[],
): Promise<{ ciphertext: ArrayBuffer; iv: Uint8Array }> => {
  const json = JSON.stringify(elements);
  const encoded = new TextEncoder().encode(json);
  const { encryptedBuffer, iv } = await encryptData(key, encoded);
  return { ciphertext: encryptedBuffer, iv };
};

const decryptElementsFromRow = async (
  iv: string,
  ciphertext: string,
  roomKey: string,
): Promise<readonly ExcalidrawElement[]> => {
  const ivBytes = base64ToBytes(iv) as Uint8Array<ArrayBuffer>;
  const ciphertextBytes = base64ToBytes(ciphertext) as Uint8Array<ArrayBuffer>;
  const decrypted = await decryptData(ivBytes, ciphertextBytes, roomKey);
  return JSON.parse(new TextDecoder("utf-8").decode(new Uint8Array(decrypted)));
};

// ─────────────────────────────────────────────────────────────
// Scene version cache
// ─────────────────────────────────────────────────────────────

class SupabaseSceneVersionCache {
  private static cache = new WeakMap<Socket, number>();
  static get = (socket: Socket) => SupabaseSceneVersionCache.cache.get(socket);
  static set = (
    socket: Socket,
    elements: readonly SyncableExcalidrawElement[],
  ) => {
    SupabaseSceneVersionCache.cache.set(socket, getSceneVersion(elements));
  };
}

export const isSavedToSupabase = (
  portal: Portal,
  elements: readonly ExcalidrawElement[],
): boolean => {
  if (portal.socket && portal.roomId && portal.roomKey) {
    return (
      SupabaseSceneVersionCache.get(portal.socket) === getSceneVersion(elements)
    );
  }
  return true;
};

// ─────────────────────────────────────────────────────────────
// Load scene
// ─────────────────────────────────────────────────────────────

export const loadFromSupabase = async (
  roomId: string,
  roomKey: string,
  socket: Socket | null,
): Promise<readonly OrderedExcalidrawElement[] | null> => {
  const row = await supabaseSelect<{
    iv: string;
    ciphertext: string;
    scene_version: number;
  }>(ROOMS_TABLE, `id=eq.${roomId}`);

  if (!row) return null;

  const elements = await decryptElementsFromRow(
    row.iv,
    row.ciphertext,
    roomKey,
  );

  if (socket) {
    const syncable = getSyncableElements(
      elements as OrderedExcalidrawElement[],
    );
    SupabaseSceneVersionCache.set(socket, syncable);
  }

  return restoreElements(
    elements,
    null,
  ) as unknown as readonly OrderedExcalidrawElement[];
};

// ─────────────────────────────────────────────────────────────
// Save scene
// ─────────────────────────────────────────────────────────────

export const saveToSupabase = async (
  portal: Portal,
  elements: readonly SyncableExcalidrawElement[],
  appState: AppState,
): Promise<readonly RemoteExcalidrawElement[] | false> => {
  const { roomId, roomKey, socket } = portal;
  if (!roomId || !roomKey || !socket) return false;

  const sceneVersion = getSceneVersion(elements);
  if (SupabaseSceneVersionCache.get(socket) === sceneVersion) {
    return false; // nothing changed
  }

  const { ciphertext, iv } = await encryptElements(roomKey, elements);
  const ivBase64 = bytesToBase64(new Uint8Array(iv));
  const ciphertextBase64 = bytesToBase64(new Uint8Array(ciphertext));

  const ok = await supabaseUpsert(ROOMS_TABLE, {
    id: roomId,
    iv: ivBase64,
    ciphertext: ciphertextBase64,
    scene_version: sceneVersion,
    updated_at: new Date().toISOString(),
  });

  if (!ok) return false;

  SupabaseSceneVersionCache.set(socket, elements);
  return elements as unknown as readonly RemoteExcalidrawElement[];
};

// ─────────────────────────────────────────────────────────────
// Files
// ─────────────────────────────────────────────────────────────

export const saveFilesToSupabase = async ({
  prefix,
  files,
}: {
  prefix: string;
  files: { id: FileId; buffer: Uint8Array }[];
}) => {
  const erroredFiles: FileId[] = [];
  const savedFiles: FileId[] = [];

  await Promise.all(
    files.map(async ({ id, buffer }) => {
      // Store as base64 in the files table
      const base64 = bytesToBase64(buffer);
      const ok = await supabaseUpsert(FILES_TABLE, {
        id: `${prefix}/${id}`,
        data_base64: base64,
        created_at: new Date().toISOString(),
      });
      if (ok) {
        savedFiles.push(id);
      } else {
        erroredFiles.push(id);
      }
    }),
  );

  return { savedFiles, erroredFiles };
};

export const loadFilesFromSupabase = async (
  prefix: string,
  decryptionKey: string,
  filesIds: readonly FileId[],
) => {
  const loadedFiles: BinaryFileData[] = [];
  const erroredFiles = new Map<FileId, true>();

  await Promise.all(
    [...new Set(filesIds)].map(async (id) => {
      try {
        const row = await supabaseSelect<{
          data_base64: string;
          mime_type?: string;
        }>(FILES_TABLE, `id=eq.${encodeURIComponent(`${prefix}/${id}`)}`);
        if (!row || !row.data_base64) {
          erroredFiles.set(id, true);
          return;
        }

        const buffer = base64ToBytes(row.data_base64);
        const { data, metadata } = await decompressData<BinaryFileMetadata>(
          buffer,
          { decryptionKey },
        );

        const dataURL: DataURL = new TextDecoder().decode(data) as DataURL;
        loadedFiles.push({
          mimeType: metadata.mimeType ?? MIME_TYPES.binary,
          id: toBrandedType<FileId>(id),
          dataURL,
          created: metadata?.created ?? Date.now(),
          lastRetrieved: Date.now(),
        });
      } catch {
        erroredFiles.set(id, true);
      }
    }),
  );

  return { loadedFiles, erroredFiles };
};
